/**
 * IntakeForm — Step 1 of starting a new service request.
 *
 * Two flavours, picked by `serviceTypeSnake`:
 *
 *   any standard service (e.g. "itr_filing")
 *     → financial year (required) · portal password (optional)
 *
 *   "other" — customer-defined service
 *     → custom service name (required, ≥3 chars after trim)
 *     → custom service description (required, ≥10 chars after trim, ≤500)
 *     → financial year (optional but pre-filled — backend allows null)
 *     → portal password (optional)
 *
 * Identity capture (v2 spec, May 2026):
 *   - If the profile has no PAN, show an editable PAN input (required).
 *   - If the profile has no DOB, show an editable DOB input (required).
 *   - Otherwise PAN is shown read-only from the customer's profile and DOB is
 *     hidden. Backend saves the first-time PAN/DOB submission back to the
 *     profile and rejects mismatched re-submits.
 *
 * On submit → calls createServiceRequest → parent navigates to the new SR.
 */
import * as React from 'react';
import {
  Eye, EyeOff, Lock, User, ArrowRight, Loader2, CalendarRange,
  Sparkles, MessageSquareText, CalendarDays,
} from 'lucide-react';
import { toast, FancySelect, DateField, type FancySelectOption } from '@finmin/ui';
import { ApiError } from '@finmin/api-client';
import { useProfile } from '@/api/profile';
import type { StageQuestionItem } from '@/api/services';
import {
  getPanItemState,
  isValidPanFormat,
  normalizePan,
  panDisplayName,
  panTypeColor,
  panTypeLabel,
  useAddPAN,
} from '@/api/customer-pans';
import {
  useCreateServiceRequest,
  useFinancialYears,
  type ServiceRequestCreateRequest,
} from '@/api/service-requests';
import { getServiceMeta } from '@/features/dashboard/constants';
import { usePanView } from '@/features/dashboard/pan-view-context';
import { PALETTE_FOREST, PALETTE_AMBER, PALETTE_MINT } from '../tokens';

// Custom-service input bounds — match server validation: name non-empty,
// description non-empty, both capped at 500 chars per OpenAPI string schemas.
const CUSTOM_NAME_MIN  = 3;
const CUSTOM_NAME_MAX  = 80;
const CUSTOM_DESC_MIN  = 10;
const CUSTOM_DESC_MAX  = 500;

interface IntakeFormProps {
  /** snake_case service type, e.g. "itr_filing" or "other". */
  serviceTypeSnake: string;
  /** PAN entity types this service is offered to; empty while the catalog loads. */
  panTypes: string[];
  /** Called with the new service-request ID on success. */
  onSuccess: (id: string) => void;
  /** Stage questions for stage="service_selected", asked before submitting. */
  stageQuestions?: StageQuestionItem[];
  /** True while the service catalog (and thus `stageQuestions`) is still
   *  loading — blocks submit so essential-question validation can't be
   *  bypassed by submitting before the real questions arrive. */
  catalogLoading: boolean;
}

export function IntakeForm({
  serviceTypeSnake,
  panTypes,
  onSuccess,
  stageQuestions = [],
  catalogLoading,
}: IntakeFormProps) {
  const { data: profile } = useProfile();
  const serviceMeta = getServiceMeta(serviceTypeSnake);
  const Icon = serviceMeta.Icon;

  const isOther = serviceTypeSnake === 'other';

  const { data: financialYears = [], isLoading: fyLoading } = useFinancialYears();
  const [financialYear, setFinancialYear] = React.useState('');
  const [password,      setPassword]      = React.useState('');
  const [showPassword,  setShowPassword]  = React.useState(false);
  const [fyError,       setFyError]       = React.useState<string | null>(null);
  const [onboardingAnswers, setOnboardingAnswers] = React.useState<Record<string, string>>({});

  // The list runs next year first, so the current year is the sensible default.
  React.useEffect(() => {
    if (!financialYear && financialYears.length > 0) {
      setFinancialYear(financialYears[1] ?? financialYears[0]!);
    }
  }, [financialYear, financialYears]);

  const FY_OPTIONS: FancySelectOption<string>[] = financialYears.map((fy, idx) => ({
    value: fy,
    label: fy,
    hint: idx === 1 ? 'Current' : idx === 0 ? 'Upcoming' : undefined,
  }));

  // ── Identity capture (v2): collect missing DOB inline; select a PAN ──────
  const profileDob = profile?.dob ?? '';
  const needDob    = !profileDob;

  // The PAN is the app-wide active one, so the nav switcher and this form
  // always agree; choosing here switches the active PAN too.
  const { selection, setSelection, pans, constraint } = usePanView();
  const needNewPan  = pans.length === 0;
  const selectedPanId = selection === 'all' ? '' : selection;
  const setSelectedPanId = setSelection;

  const isEligible = React.useCallback(
    (panType: string) => panTypes.length === 0 || panTypes.includes(panType),
    [panTypes],
  );

  // With one eligible PAN there is nothing to choose — select it automatically.
  const eligiblePans = pans.filter((p) => isEligible(p.pan_type));
  React.useEffect(() => {
    if (selection === 'all' && panTypes.length > 0 && eligiblePans.length === 1) {
      setSelection(eligiblePans[0]!.id);
    }
  }, [selection, panTypes, eligiblePans, setSelection]);

  const [newPan,    setNewPan]  = React.useState('');
  const [dob,       setDob]     = React.useState('');
  const [panError,  setPanError] = React.useState<string | null>(null);
  const [dobError,  setDobError] = React.useState<string | null>(null);

  const addPan = useAddPAN();

  // ── Custom-service-only state ─────────────────────────────────────────────
  const [customName,        setCustomName]        = React.useState('');
  const [customDescription, setCustomDescription] = React.useState('');
  const [customNameError,   setCustomNameError]   = React.useState<string | null>(null);
  const [customDescError,   setCustomDescError]   = React.useState<string | null>(null);

  const { mutate: createRequest, isPending } = useCreateServiceRequest({
    onSuccess: (data) => {
      toast.success('Request received! Our team will reach out shortly.');
      // Brief pause before navigating — gives the backend time to commit so
      // the subsequent GET on the detail page does not race to a 404.
      setTimeout(() => onSuccess(data.id), 500);
    },
    onError: (err) => {
      toast.error(err.message ?? 'Could not start this service. Please try again.');
    },
  });

  function validate(): boolean {
    let ok = true;

    if (!financialYear) {
      setFyError('Please select a financial year.');
      ok = false;
    } else {
      setFyError(null);
    }

    if (needNewPan) {
      if (!newPan.trim()) {
        setPanError('PAN is required.');
        ok = false;
      } else if (!isValidPanFormat(newPan)) {
        setPanError('Enter a valid PAN (e.g. ABCDE1234F).');
        ok = false;
      } else {
        setPanError(null);
      }
    } else if (!selectedPanId) {
      setPanError('Select which PAN to file under.');
      ok = false;
    } else {
      const selected = pans.find((p) => p.id === selectedPanId);
      if (selected && !isEligible(selected.pan_type)) {
        setPanError(
          `${panTypeLabel(selected.pan_type)} PANs cannot use ${serviceMeta.title}.`,
        );
        ok = false;
      } else {
        setPanError(null);
      }
    }

    if (needDob) {
      if (!dob) {
        setDobError('Date of birth is required.');
        ok = false;
      } else {
        const d = new Date(dob);
        if (isNaN(d.getTime()) || d > new Date()) {
          setDobError('Please enter a valid date of birth.');
          ok = false;
        } else {
          setDobError(null);
        }
      }
    }

    if (isOther) {
      const name = customName.trim();
      const desc = customDescription.trim();

      if (name.length < CUSTOM_NAME_MIN) {
        setCustomNameError(
          `Service name must be at least ${CUSTOM_NAME_MIN} characters.`,
        );
        ok = false;
      } else {
        setCustomNameError(null);
      }

      if (desc.length < CUSTOM_DESC_MIN) {
        setCustomDescError(
          `Please describe your need in at least ${CUSTOM_DESC_MIN} characters so we can help.`,
        );
        ok = false;
      } else {
        setCustomDescError(null);
      }
    }

    return ok;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    let customerPanId = selectedPanId;
    if (needNewPan) {
      try {
        const created = await addPan.mutateAsync({ pan: normalizePan(newPan) });
        customerPanId = created.id;
        // The customer's first PAN becomes what the rest of the app is scoped to.
        setSelection(created.id);
      } catch (err) {
        setPanError(err instanceof ApiError ? err.message : 'Could not save PAN. Please try again.');
        return;
      }
    }

    const answeredEntries = Object.entries(onboardingAnswers).filter(([, v]) => v.trim());

    const payload: ServiceRequestCreateRequest = {
      customer_pan_id:    customerPanId,
      financial_year:     financialYear,
      customer_password:  password.trim() || null,
      ...(needDob && { dob }),
      ...(isOther && {
        custom_service_name:        customName.trim(),
        custom_service_description: customDescription.trim(),
      }),
      ...(answeredEntries.length > 0 && {
        onboarding_answers: Object.fromEntries(answeredEntries),
      }),
    };

    createRequest({ serviceTypeSnake, payload });
  }

  const essentialsMissing = stageQuestions
    .filter((q) => q.is_essential)
    .some((q) => !onboardingAnswers[q.label]?.trim());

  return (
    <div className="w-full max-w-md mx-auto px-4 py-10">

      {/* Service badge */}
      <div className="flex flex-col items-center gap-3 mb-8">
        <div
          className="h-14 w-14 rounded-2xl flex items-center justify-center shadow-sm"
          style={{ backgroundColor: PALETTE_MINT }}
        >
          <Icon className="h-7 w-7" style={{ color: PALETTE_FOREST }} aria-hidden="true" />
        </div>
        <div className="text-center">
          <h1 className="text-xl font-bold text-foreground">
            {isOther ? 'Tell us what you need' : serviceMeta.title}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isOther
              ? 'A few details and our team will get back with a tailored quote.'
              : 'Enter your details to get started'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} noValidate>
        <div
          className="rounded-2xl border border-border p-6 sm:p-8 space-y-6 shadow-sm"
          style={{ backgroundColor: 'hsl(var(--background))' }}
        >

          {/* ── Custom-service-only fields ────────────────────────────────── */}
          {isOther && (
            <>
              <CustomServiceNameField
                value={customName}
                onChange={(v) => { setCustomName(v); setCustomNameError(null); }}
                error={customNameError}
              />
              <CustomServiceDescriptionField
                value={customDescription}
                onChange={(v) => { setCustomDescription(v); setCustomDescError(null); }}
                error={customDescError}
              />
            </>
          )}

          {/* Stage questions for stage="service_selected" — asked before PAN/FY */}
          {stageQuestions.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm font-semibold" style={{ color: PALETTE_FOREST }}>
                A few questions before we start
              </p>
              {stageQuestions.map((q) => (
                <div key={q.label} className="space-y-1">
                  <label className="block text-sm font-medium text-foreground">
                    {q.label}
                    {q.is_essential && <span className="ml-0.5 text-destructive">*</span>}
                  </label>
                  {q.answer_type === 'text' && (
                    <input
                      type="text"
                      value={onboardingAnswers[q.label] ?? ''}
                      onChange={(e) =>
                        setOnboardingAnswers((prev) => ({ ...prev, [q.label]: e.target.value }))
                      }
                      className="h-10 w-full rounded-xl border border-border bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      placeholder={q.is_essential ? 'Required' : 'Optional'}
                    />
                  )}
                  {q.answer_type === 'multiple_choice' && (
                    <div className="flex flex-wrap gap-2">
                      {(q.options ?? []).map((opt) => (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => setOnboardingAnswers((prev) => ({ ...prev, [q.label]: opt }))}
                          className="h-9 rounded-lg border px-3 text-sm"
                          style={{
                            borderColor: onboardingAnswers[q.label] === opt ? PALETTE_FOREST : undefined,
                            backgroundColor: onboardingAnswers[q.label] === opt ? `${PALETTE_FOREST}12` : undefined,
                          }}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  )}
                  {q.answer_type === 'true_false' && (
                    <div className="flex gap-2">
                      {(['true', 'false'] as const).map((v) => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => setOnboardingAnswers((prev) => ({ ...prev, [q.label]: v }))}
                          className="h-9 flex-1 rounded-lg border text-sm capitalize"
                          style={{
                            borderColor: onboardingAnswers[q.label] === v ? PALETTE_FOREST : undefined,
                            backgroundColor: onboardingAnswers[q.label] === v ? `${PALETTE_FOREST}12` : undefined,
                          }}
                        >
                          {v === 'true' ? 'Yes' : 'No'}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* PAN — add inline if none exist, read-only if exactly one, pick if several */}
          {needNewPan ? (
            <div>
              <label htmlFor="pan" className="block text-sm font-medium text-foreground mb-1.5">
                PAN Number
                <span className="text-destructive ml-0.5">*</span>
              </label>
              <div className="relative">
                <User
                  className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none"
                  aria-hidden="true"
                />
                <input
                  id="pan"
                  type="text"
                  value={newPan}
                  onChange={(e) => { setNewPan(e.target.value.toUpperCase().slice(0, 10)); setPanError(null); }}
                  maxLength={10}
                  placeholder="ABCDE1234F"
                  autoCapitalize="characters"
                  className="w-full rounded-lg border border-border bg-background pl-9 pr-3 py-2.5 text-sm font-mono tracking-wider text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring uppercase"
                  style={{ borderColor: panError ? 'hsl(var(--destructive))' : undefined }}
                />
              </div>
              {panError
                ? <p className="text-xs text-destructive mt-1">{panError}</p>
                : <p className="text-[11px] text-muted-foreground mt-1">
                    Saved to your profile for future filings.
                  </p>
              }
            </div>
          ) : pans.length === 1 ? (
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                PAN Number
              </label>
              <div className="relative">
                <User
                  className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none"
                  aria-hidden="true"
                />
                <input
                  type="text"
                  readOnly
                  value={pans[0]!.pan}
                  className="w-full rounded-lg border border-border bg-muted/40 pl-9 pr-3 py-2.5 text-sm font-mono tracking-wider text-foreground/70 cursor-not-allowed uppercase"
                  aria-label="PAN (read-only)"
                />
              </div>
              {isEligible(pans[0]!.pan_type) ? (
                <p className="text-[11px] text-muted-foreground mt-1">
                  Sourced from your profile. Manage your PANs in{' '}
                  <a href="/profile" className="underline underline-offset-2" style={{ color: PALETTE_FOREST }}>
                    Profile settings
                  </a>.
                </p>
              ) : (
                <p className="text-xs text-destructive mt-1">
                  {panTypeLabel(pans[0]!.pan_type)} PANs cannot use {serviceMeta.title}. Add a
                  suitable PAN in{' '}
                  <a href="/profile" className="underline underline-offset-2">
                    Profile settings
                  </a>.
                </p>
              )}
            </div>
          ) : (
            <div>
              <label htmlFor="pan-select" className="block text-sm font-medium text-foreground mb-1.5">
                Which PAN is this for?
                <span className="text-destructive ml-0.5">*</span>
              </label>
              <FancySelect
                id="pan-select"
                options={pans.map((p): FancySelectOption<string> => {
                  const { disabled, reason } = getPanItemState(p, constraint);
                  const named = Boolean(p.label?.trim());
                  return {
                    value: p.id,
                    label: panDisplayName(p),
                    hint: (
                      <span style={{ color: panTypeColor(p.pan_type) }}>
                        {disabled && reason
                          ? reason
                          : // Keep the PAN visible when a name replaces it —
                            // this is the moment the filing target matters.
                            named
                            ? `${p.pan} · ${panTypeLabel(p.pan_type)}`
                            : panTypeLabel(p.pan_type)}
                      </span>
                    ),
                    disabled,
                  };
                })}
                value={selectedPanId}
                onChange={setSelectedPanId}
                invalid={!!panError}
                leadingIcon={User}
                ariaLabel="Select PAN"
                placeholder="Choose a PAN…"
              />
              {panError && <p className="text-xs text-destructive mt-1">{panError}</p>}
            </div>
          )}

          {/* DOB — only when profile is missing it */}
          {needDob && (
            <div>
              <label htmlFor="dob" className="block text-sm font-medium text-foreground mb-1.5">
                Date of Birth
                <span className="text-destructive ml-0.5">*</span>
              </label>
              <DateField
                id="dob"
                value={dob}
                onChange={(v) => { setDob(v); setDobError(null); }}
                max={new Date().toISOString().split('T')[0]}
                invalid={!!dobError}
                placeholder="DD / MM / YYYY"
                ariaLabel="Date of birth"
              />
              {dobError
                ? <p className="text-xs text-destructive mt-1">{dobError}</p>
                : <p className="text-[11px] text-muted-foreground mt-1">
                    Saved to your profile for future filings.
                  </p>
              }
            </div>
          )}

          {/* Financial Year */}
          <div>
            <label htmlFor="financial_year" className="block text-sm font-medium text-foreground mb-1.5">
              Financial Year
              {!isOther && <span className="text-destructive ml-0.5">*</span>}
              {isOther && (
                <span className="ml-1.5 text-xs text-muted-foreground font-normal">(if relevant)</span>
              )}
            </label>
            <FancySelect
              id="financial_year"
              options={FY_OPTIONS}
              value={financialYear}
              onChange={(v) => { setFinancialYear(v); setFyError(null); }}
              invalid={!!fyError}
              leadingIcon={CalendarRange}
              ariaLabel="Financial Year"
              disabled={fyLoading}
              placeholder={fyLoading ? 'Loading…' : 'Choose a financial year…'}
            />
            {fyError && <p className="text-xs text-destructive mt-1">{fyError}</p>}
          </div>

          {/* Portal password (optional) */}
          <div>
            <label htmlFor="portal_password" className="block text-sm font-medium text-foreground mb-1.5">
              Income Tax Portal Password
              <span className="ml-1.5 text-xs text-muted-foreground font-normal">(optional)</span>
            </label>
            <div className="relative">
              <Lock
                className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none"
                aria-hidden="true"
              />
              <input
                id="portal_password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Leave blank to skip"
                className="w-full rounded-lg border border-border bg-background pl-9 pr-10 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword
                  ? <EyeOff className="h-4 w-4" aria-hidden="true" />
                  : <Eye    className="h-4 w-4" aria-hidden="true" />
                }
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">
              Helps us retrieve your tax data faster. Stored encrypted, never shared.
            </p>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isPending || addPan.isPending || essentialsMissing || catalogLoading}
            className="w-full h-11 rounded-xl flex items-center justify-center gap-2 text-sm font-semibold text-white transition-opacity disabled:opacity-60 active:scale-[0.99]"
            style={{ backgroundColor: (isPending || addPan.isPending) ? 'hsl(var(--muted))' : PALETTE_AMBER }}
          >
            {(isPending || addPan.isPending) ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                {isOther ? 'Send my request' : 'Register for service'}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </>
            )}
          </button>
        </div>

        {/* Trust signals */}
        <div className="flex items-center justify-center gap-4 mt-5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60 flex-wrap">
          <span className="flex items-center gap-1"><Lock className="h-3 w-3" /> ERI Secured</span>
          <span className="w-1 h-1 rounded-full bg-border" />
          <span>AI Assisted</span>
          <span className="w-1 h-1 rounded-full bg-border" />
          <span>Expert Reviewed</span>
        </div>
      </form>
    </div>
  );
}

// ── Custom-service field components ─────────────────────────────────────────

function CustomServiceNameField({
  value, onChange, error,
}: {
  value: string;
  onChange: (v: string) => void;
  error: string | null;
}) {
  return (
    <div>
      <label
        htmlFor="custom_service_name"
        className="block text-sm font-medium text-foreground mb-1.5"
      >
        What service do you need?
        <span className="text-destructive ml-0.5">*</span>
      </label>
      <div className="relative">
        <Sparkles
          className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none"
          aria-hidden="true"
        />
        <input
          id="custom_service_name"
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value.slice(0, CUSTOM_NAME_MAX))}
          maxLength={CUSTOM_NAME_MAX}
          placeholder="e.g. GST notice consultation"
          className="w-full rounded-lg border border-border bg-background pl-9 pr-14 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          style={{ borderColor: error ? 'hsl(var(--destructive))' : undefined }}
        />
        <span
          className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-mono text-muted-foreground/60 tabular-nums pointer-events-none"
          aria-hidden
        >
          {value.length}/{CUSTOM_NAME_MAX}
        </span>
      </div>
      {error
        ? <p className="text-xs text-destructive mt-1">{error}</p>
        : (
          <p className="text-[11px] text-muted-foreground mt-1">
            A short title — what kind of help you need.
          </p>
        )
      }
    </div>
  );
}

function CustomServiceDescriptionField({
  value, onChange, error,
}: {
  value: string;
  onChange: (v: string) => void;
  error: string | null;
}) {
  return (
    <div>
      <label
        htmlFor="custom_service_description"
        className="block text-sm font-medium text-foreground mb-1.5"
      >
        Tell us a bit more
        <span className="text-destructive ml-0.5">*</span>
      </label>
      <div className="relative">
        <MessageSquareText
          className="absolute left-3 top-3 h-4 w-4 text-muted-foreground pointer-events-none"
          aria-hidden="true"
        />
        <textarea
          id="custom_service_description"
          value={value}
          onChange={(e) => onChange(e.target.value.slice(0, CUSTOM_DESC_MAX))}
          maxLength={CUSTOM_DESC_MAX}
          rows={4}
          placeholder="What is the situation? Any deadlines? Any documents you already have?"
          className="w-full rounded-lg border border-border bg-background pl-9 pr-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none leading-relaxed"
          style={{ borderColor: error ? 'hsl(var(--destructive))' : undefined }}
        />
      </div>
      <div className="flex items-center justify-between gap-2 mt-1">
        <p
          className="text-[11px]"
          style={{ color: error ? 'hsl(var(--destructive))' : 'hsl(var(--muted-foreground))' }}
        >
          {error ?? 'The more context, the faster we can help.'}
        </p>
        <span
          className="text-[10px] font-mono text-muted-foreground/60 tabular-nums shrink-0"
          aria-hidden
        >
          {value.length}/{CUSTOM_DESC_MAX}
        </span>
      </div>
    </div>
  );
}
